import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';
import { EditMedicalGroupDialogProps } from '../../utils/DataTypes';
import { MEDICAL_GROUPS_STRINGS } from '../../constants';
import { useDataProvider } from 'react-admin';
import { toast } from 'react-toastify';

const EditMedicalGroupDialog: React.FC<EditMedicalGroupDialogProps> = ({
  isOpen,
  formValues,
  originalValues,
  setFormValues,
  onClose,
  onSave,
}) => {
  const dataProvider = useDataProvider();
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
    }
  }, [isOpen, formValues.medical_group_number, formValues.medical_group_name]);

  const handleDiscardChanges = () => {
    setFormValues(originalValues);
    setErrorMessage(null);
    toast.info('Changes discarded. Values restored to original.');
  };

  const handleValidationAndSave = async () => {
    setErrorMessage(null);

    const cleanNumber = formValues.medical_group_number?.trim() || '';
    const cleanName = formValues.medical_group_name?.trim() || '';

    if (!cleanNumber || !cleanName) {
      setErrorMessage('Medical Group Number and Name are required.');
      return;
    }

    if (cleanNumber === originalValues.medical_group_number.trim()) {
      await onSave();
      return;
    }

    setIsValidating(true);
    try {
      const { data: duplicates } = await dataProvider.getList('ValidateMedicalGroups', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: formValues.billing_client_number,
          medical_group_numbers: [cleanNumber],
        },
      });

      if (duplicates.length > 0) {
        setErrorMessage(`Medical Group ${cleanNumber} is already taken by another group.`);
        setIsValidating(false);
        return;
      }

      await onSave();
    } catch (err) {
      toast.error('Validation failed.');
    } finally {
      setIsValidating(false);
    }
  };

  const hasChanges = JSON.stringify(formValues) !== JSON.stringify(originalValues);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{MEDICAL_GROUPS_STRINGS.LABELS.EDIT_DIALOG_TITLE}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box display="flex" gap={2} alignItems="center" mb={3}>
            <TextField label="Client Name" value={formValues.billing_client_name} fullWidth disabled />
            <TextField
              label="Client Number"
              value={formValues.billing_client_number || ''}
              disabled
              sx={{ width: '200px' }}
            />
          </Box>
          <TextField
            label={MEDICAL_GROUPS_STRINGS.LABELS.MEDICAL_GROUP_NUMBER}
            value={formValues.medical_group_number}
            error={!!errorMessage && !formValues.medical_group_number?.trim()}
            onChange={(e) =>
              setFormValues((prev: any) => ({
                ...prev,
                medical_group_number: e.target.value,
              }))
            }
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label={MEDICAL_GROUPS_STRINGS.LABELS.MEDICAL_GROUP_NAME}
            value={formValues.medical_group_name}
            error={!!errorMessage && !formValues.medical_group_name?.trim()}
            onChange={(e) =>
              setFormValues((prev: any) => ({
                ...prev,
                medical_group_name: e.target.value,
              }))
            }
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label={MEDICAL_GROUPS_STRINGS.LABELS.PLAN_LOCATION}
            value={formValues.plan_location}
            onChange={(e) =>
              setFormValues((prev: any) => ({
                ...prev,
                plan_location: e.target.value,
              }))
            }
            fullWidth
            variant="outlined"
          />

          {errorMessage && (
            <Box
              p={1.5}
              sx={{
                backgroundColor: '#fff5f5',
                border: '1px solid #ffcccc',
                borderRadius: 1,
                mt: 1,
              }}
            >
              <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
                ⚠️ {errorMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleDiscardChanges} color="inherit" disabled={!hasChanges || isValidating}>
          Restore Original
        </Button>
        <Box>
          <Button onClick={onClose} disabled={isValidating}>
            {MEDICAL_GROUPS_STRINGS.LABELS.CANCEL_BUTTON}
          </Button>
          <Button
            onClick={handleValidationAndSave}
            color="primary"
            variant="contained"
            disabled={!hasChanges || isValidating}
          >
            {isValidating ? 'Validating...' : MEDICAL_GROUPS_STRINGS.LABELS.SAVE_BUTTON}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditMedicalGroupDialog;
